import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export const QueueTab = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [zoom, setZoom] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/deposits")
      .then((r) => setRows(r.data))
      .catch(() => {
        setRows([]);
        toast.error("Could not load deposits");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, action) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/deposits/${id}/review`, { action });
      toast.success(
        action === "approve"
          ? data.plan_status === "confirmed"
            ? "Approved — both deposits in, plan is now CONFIRMED"
            : "Deposit approved"
          : "Deposit rejected — payer can re-upload"
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    } finally {
      setBusy(null);
    }
  };

  if (rows === null) return <Skeleton className="h-40 w-full rounded-xl bg-[#15151A]" />;
  const pending = rows.filter((r) => r.status === "pending_review");

  return (
    <div className="space-y-4" data-testid="admin-queue-tab">
      {pending.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-[#D4AF37]/25 rounded-xl" data-testid="admin-queue-empty">
          <p className="text-sm text-[#A0A0A0]">No deposits awaiting verification.</p>
        </div>
      ) : (
        pending.map((d) => (
          <div
            key={d.deposit_id}
            data-testid="admin-queue-row"
            className="bg-[#15151A] border border-[#D4AF37]/20 rounded-xl p-4 flex flex-col sm:flex-row gap-4"
          >
            <button onClick={() => setZoom(d.screenshot)} data-testid="admin-queue-screenshot" className="shrink-0 self-start">
              <img
                src={d.screenshot}
                alt="Payment proof"
                className="w-24 h-24 object-cover rounded-md border border-white/10 hover:border-[#D4AF37]/60 transition-colors"
              />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium flex items-center">
                {d.payer_name}
                <Badge className="ml-2 bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] uppercase">
                  {d.role}
                </Badge>
              </div>
              <p className="text-xs text-[#A0A0A0] mt-1">
                {d.plan_title} · {d.plan_neighborhood}
              </p>
              <p className="font-serif text-xl text-[#D4AF37] mt-1" data-testid="admin-queue-amount">
                {inr(d.amount)}
              </p>
            </div>
            <div className="flex sm:flex-col gap-2 justify-end">
              <Button
                size="sm"
                disabled={busy === d.deposit_id}
                onClick={() => review(d.deposit_id, "approve")}
                data-testid="admin-approve-button"
                className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold transition-colors"
              >
                <Check className="w-4 h-4 mr-1" strokeWidth={2} /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy === d.deposit_id}
                onClick={() => review(d.deposit_id, "reject")}
                data-testid="admin-reject-button"
                className="border-[#E53935]/60 text-[#E53935] hover:bg-[#E53935]/10 hover:text-[#E53935] bg-transparent transition-colors"
              >
                <X className="w-4 h-4 mr-1" strokeWidth={2} /> Reject
              </Button>
            </div>
          </div>
        ))
      )}
      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="bg-[#15151A] border-[#D4AF37]/30 max-w-lg" data-testid="admin-screenshot-dialog">
          <DialogTitle className="font-serif text-white">Payment Screenshot</DialogTitle>
          <DialogDescription className="text-[#A0A0A0] text-xs">
            Uploaded payment proof for verification.
          </DialogDescription>
          {zoom && <img src={zoom} alt="Payment proof" className="w-full rounded-md" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
